import React from 'react';
import type { FieldValues } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  getColumns as getCoreCostDelegatedColumns,
  getRows as getCoreCostDelegatedRows,
} from '../../components/form/CoreCostDelegatedInput';
import {
  getColumns as getCoreCostExternalColumns,
  getRows as getCoreCostExternalRows,
} from '../../components/form/CoreCostExternalInput';
import {
  getColumns as getCoreCostInteralColumns,
  getRows as getCoreCostInteralRows,
} from '../../components/form/CoreCostInteralInput';
import {
  getColumns as getCoreCostSCSColumns,
  getRows as getCoreCostSCSRows,
} from '../../components/form/CoreCostSCSInput';
import {
  getColumns as getCoreCphConsolidatedColumns,
  getRows as getCoreCphConsolidatedRows,
} from '../../components/form/CoreCphConsolidatedInput';
import {
  getColumns as getCoreNpsConsolidatedColumns,
  getRows as getCoreNpsConsolidatedRows,
} from '../../components/form/CoreNpsConsolidatedInput';
import {
  getColumns as getCoreNpsDelegatedColumns,
  getRows as getCoreNpsDelegatedRows,
} from '../../components/form/CoreNpsDelegatedInput';
import {
  getColumns as getCoreNpsSCSColumns,
  getRows as getCoreNpsSCSRows,
} from '../../components/form/CoreNpsSCSInput';
import {
  getColumns as getCoreTtfConsolidatedColumns,
  getRows as getCoreTtfConsolidatedRows,
} from '../../components/form/CoreTtfConsolidatedInput';
import {
  getColumns as getCoreTthConsolidatedColumns,
  getRows as getCoreTthConsolidatedRows,
} from '../../components/form/CoreTthConsolidatedInput';
import {
  getColumns as getCoreTthDelegatedColumns,
  getRows as getCoreTthDelegatedRows,
} from '../../components/form/CoreTthDelegatedInput';
import {
  getColumns as getCoreTthSCSColumns,
  getRows as getCoreTthSCSRows,
} from '../../components/form/CoreTthSCSInput';
import {
  getColumns as getCoreVacConsolidatedColumns,
  getRows as getCoreVacConsolidatedRows,
} from '../../components/form/CoreVacConsolidatedInput';
import {
  getColumns as getDisabilityInputColumns,
  getRows as getDisabilityInputRows,
} from '../../components/form/DisabilityInput';
import {
  getColumns as getEthnicityInputColumns,
  getRows as getEthnicityInputRows,
} from '../../components/form/EthnicityInput';
import DynamicTableSummary from '../../components/form/DynamicTableSummary';
import {
  getColumns as getExceptionBreakdownColumns,
  getRows as getExceptionBreakdownRows,
} from '../../components/form/ExceptionBreakdownInput';
import {
  getColumns as getGenderInputColumns,
  getRows as getGenderInputRows,
} from '../../components/form/GenderInput';
import {
  getColumns as getBreachesColumns,
  getRows as getBreachesRows,
} from '../../components/form/InputBreaches';
import {
  getColumns as getExternalCompetitionsColumns,
  getRows as getExternalCompetitionsRows,
} from '../../components/form/InputExternalCompetitions';
import {
  getColumns as getExternalExemptionColumns,
  getRows as getExternalExemptionRows,
} from '../../components/form/InputExternalExemptions';
import {
  getColumns as getFairAndOpenColumns,
  getRows as getFairAndOpenRows,
} from '../../components/form/InputFairAndOpen';
import {
  getColumns as getRecruitmentCampaignsColumns,
  getRows as getRecruitmentCampaignsRows,
} from '../../components/form/InputRecruitmentCampaigns';
import type { ListTotalConfig, Page, Question, Subsection } from '../../config/types';
import { useFormConfig } from '../../hooks/useFormConfig';
import type { FetchFormResponse } from '../../service/types';
import { Status } from '../../types';
import {
  extractConditionalQuestions,
  getSelectedConditionalQuestions,
  hasBlockedSubmitGuardValue,
  isApprovalPageName,
  isJsonObject,
  isPageDisabled,
  isPageCompleted,
  renderAnswer,
} from '../../utils/config-utils';
import {
  formatCurrencyValue,
  formatTotalValue,
  getListAnswers,
  getListTotals,
  hasValue,
  interpolateQuestionText,
  interpolateQuestionTextWithAnswerSums,
} from '../../utils/form-utils';
import { getGdadProficiencyDerivedAnswers } from '../../utils/gdad-proficiency';
import { resolveTableauLicenseUsersAnswer } from '../../utils/tableau-license-users';
import Tag from '../Tag/Tag';

// ----------------------
// Helpers reused unchanged
// ----------------------
export const questionTypeToKeys = (
  questionType: Question['questionType'],
): { columns: string[]; rows: string[] } => {
  switch (questionType) {
    case 'exceptionBreakdownInput':
      return {
        columns: getExceptionBreakdownColumns(),
        rows: getExceptionBreakdownRows(),
      };
    case 'fairAndOpenInput':
      return { columns: getFairAndOpenColumns(), rows: getFairAndOpenRows() };
    case 'breachesInput':
      return { columns: getBreachesColumns(), rows: getBreachesRows() };
    case 'genderInput':
      return { columns: getGenderInputColumns(), rows: getGenderInputRows() };
    case 'ethnicityInput':
      return {
        columns: getEthnicityInputColumns(),
        rows: getEthnicityInputRows(),
      };
    case 'disabilityInput':
      return {
        columns: getDisabilityInputColumns(),
        rows: getDisabilityInputRows(),
      };
    case 'recruitmentCampaignInput':
      return {
        columns: getRecruitmentCampaignsColumns(),
        rows: getRecruitmentCampaignsRows(),
      };
    case 'externalExemptionInput':
      return {
        columns: getExternalExemptionColumns(),
        rows: getExternalExemptionRows(),
      };
    case 'internalCompetitionsInput':
      return {
        columns: getExternalCompetitionsColumns(),
        rows: getExternalCompetitionsRows(),
      };
    case 'coreTthDelegatedInput':
      return {
        columns: getCoreTthDelegatedColumns(),
        rows: getCoreTthDelegatedRows(),
      };
    case 'coreTthSCSInput':
      return { columns: getCoreTthSCSColumns(), rows: getCoreTthSCSRows() };
    case 'coreCostDelegatedInput':
      return {
        columns: getCoreCostDelegatedColumns(),
        rows: getCoreCostDelegatedRows(),
      };
    case 'coreCostInteralInput':
      return {
        columns: getCoreCostInteralColumns(),
        rows: getCoreCostInteralRows(),
      };
    case 'coreCostExternalInput':
      return {
        columns: getCoreCostExternalColumns(),
        rows: getCoreCostExternalRows(),
      };
    case 'coreCostSCSInput':
      return { columns: getCoreCostSCSColumns(), rows: getCoreCostSCSRows() };
    case 'coreNpsDelegatedInput':
      return {
        columns: getCoreNpsDelegatedColumns(),
        rows: getCoreNpsDelegatedRows(),
      };
    case 'coreNpsSCSInput':
      return { columns: getCoreNpsSCSColumns(), rows: getCoreNpsSCSRows() };
    case 'CoreNpsConsolidatedInput':
      return {
        columns: getCoreNpsConsolidatedColumns(),
        rows: getCoreNpsConsolidatedRows(),
      };
    case 'CoreTthConsolidatedInput':
      return {
        columns: getCoreTthConsolidatedColumns(),
        rows: getCoreTthConsolidatedRows(),
      };
    case 'CoreTtfConsolidatedInput':
      return {
        columns: getCoreTtfConsolidatedColumns(),
        rows: getCoreTtfConsolidatedRows(),
      };
    case 'CoreCphConsolidatedInput':
      return {
        columns: getCoreCphConsolidatedColumns(),
        rows: getCoreCphConsolidatedRows(),
      };
    case 'CoreVacConsolidatedInput':
      return {
        columns: getCoreVacConsolidatedColumns(),
        rows: getCoreVacConsolidatedRows(),
      };
    default:
      return { columns: [], rows: [] };
  }
};

export const labelToTitle = (label: string) => {
  if (label.toUpperCase() === 'NA') return 'N/A';
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9])/g, ' $1')
    .replace(/\bN P S\b/g, 'NPS')
    .replace(/^./, (str: string) => str.toUpperCase())
    .trim();
};

export const renderJsonObject = (answer: Record<string, string>, question: Question) => {
  const { columns, rows } = questionTypeToKeys(question.questionType);
  const rowHeader =
    question.questionType === 'coreCostInteralInput' ||
    question.questionType === 'coreCostExternalInput'
      ? 'Cost'
      : 'Grade';
  const isCoreCostTickbox =
    question.questionType === 'coreCostInteralInput' ||
    question.questionType === 'coreCostExternalInput';
  const isCoreCostSelected = (value: string | undefined) =>
    value === '1' || value?.toLowerCase() === 'yes';

  const cellStyle: React.CSSProperties = {
    padding: '6px',
    border: '1px solid #b1b4b6',
    background: 'transparent',
    color: 'inherit',
    verticalAlign: 'middle',
  };

  return (
    <table
      className="govuk-table"
      style={{
        borderCollapse: 'separate',
        borderSpacing: 0,
        margin: '8px 0',
        width: 'auto',
      }}
    >
      <thead className="govuk-table__head">
        <tr className="govuk-table__row">
          <th scope="col" className="govuk-table__header" style={cellStyle}>
            {rowHeader}
          </th>
          {columns.map((column) => (
            <th key={column} scope="col" className="govuk-table__header" style={cellStyle}>
              {labelToTitle(column)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className="govuk-table__body">
        {rows.map((row) => (
          <tr key={row} className="govuk-table__row">
            <th
              scope="row"
              className="govuk-table__header"
              style={{ ...cellStyle, textTransform: 'capitalize' }}
            >
              {labelToTitle(row)}
            </th>

            {columns.length > 0 ? (
              columns.map((column) => (
                <td
                  key={column}
                  className="govuk-table__cell"
                  style={isCoreCostTickbox ? { ...cellStyle, textAlign: 'center' } : cellStyle}
                >
                  {isCoreCostTickbox
                    ? isCoreCostSelected(answer[`${row}${column}`])
                      ? '\u2713'
                      : ''
                    : (answer[`${row}${column}`] ?? '-')}
                </td>
              ))
            ) : (
              <td
                className="govuk-table__cell"
                colSpan={Math.max(columns.length, 1)}
                style={cellStyle}
              >
                {answer[row] ?? '-'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const renderArrayAnswer = (answer: string[]) => {
  return (
    <ul className="govuk-list govuk-!-margin-0">
      {answer.map((a) => (
        <li key={a}>{a}</li>
      ))}
    </ul>
  );
};

const renderDynamicGridAnswer = (answer: unknown, question: Question) => {
  const columns = question.dynamicGrid?.columns ?? [];
  const rows = Array.isArray(answer)
    ? (answer as Record<string, unknown>[])
    : typeof answer === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(answer);
            return Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : [];
          } catch (_err) {
            return [];
          }
        })()
      : [];

  const displayValue = (value: unknown, column: (typeof columns)[number]) => {
    if (column.type === 'select' && Array.isArray(column.options)) {
      const match = column.options.find((option) => {
        if (typeof option === 'string') return option === value;
        if ('option' in option) return (option.value ?? option.option) === value;
        return false;
      });
      if (typeof match === 'string') return match;
      if (match && 'option' in match) return match.option;
    }
    if (column.type === 'number' && value !== undefined && value !== null && value !== '') {
      const numericValue = Number(String(value).replace(/[\u00a3,\s]/g, ''));
      if (Number.isFinite(numericValue)) {
        return `${column.prefix ?? ''}${numericValue.toLocaleString('en-GB', {
          maximumFractionDigits: 20,
        })}${column.suffix ?? ''}`;
      }
    }
    return String(value ?? '');
  };

  return (
    <table className="govuk-table" style={{ margin: '8px 0', width: 'auto' }}>
      <thead className="govuk-table__head">
        <tr className="govuk-table__row">
          {columns.map((column) => (
            <th key={column.field} scope="col" className="govuk-table__header">
              {column.headerName}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="govuk-table__body">
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} className="govuk-table__row">
            {columns.map((column) => (
              <td key={column.field} className="govuk-table__cell">
                {displayValue(row[column.field], column) || '-'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const formatSummaryCurrency = (value: string | number): string => {
  const rawValue = String(value).trim();
  const normalized = rawValue.replace(/[£,\s]/g, '');
  const scientificNotationPattern = /^-?\d+(?:\.\d+)?e[+-]?\d+$/i;

  if (scientificNotationPattern.test(normalized)) {
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      const formatted = parsed.toLocaleString('en-GB', { maximumFractionDigits: 20 });
      return parsed < 0 ? `-£${formatted.slice(1)}` : `£${formatted}`;
    }
  }

  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return rawValue;
  }

  const isNegative = normalized.startsWith('-');
  const unsignedValue = isNegative ? normalized.slice(1) : normalized;
  const [whole = '', fraction] = unsignedValue.split('.', 2);
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formattedNumber = fraction !== undefined ? `${formattedWhole}.${fraction}` : formattedWhole;

  return isNegative ? `-£${formattedNumber}` : `£${formattedNumber}`;
};

const getListEntries = (answers: FetchFormResponse['answers'], questions: Question[]) =>
  Object.values(getListAnswers(answers, questions)).filter((entry) =>
    entry.some(({ answer }) => hasValue(answer)),
  );

const hasRequiredListFieldsMissing = (
  entries: ReturnType<typeof getListEntries>,
): boolean =>
  entries.some((entry) =>
    entry.some(({ question, answer }) =>
      question?.rules?.required === true ? !hasValue(answer) : false,
    ),
  );

const hasPointsOfContactQueriesContact = (
  entries: ReturnType<typeof getListEntries>,
): boolean =>
  entries.some((group) =>
    group.some(({ question, answer }) => {
      if (question.name !== 'contacts-type') return false;
      return typeof answer === 'string' && answer === 'Point of contact for queries';
    }),
  );

export const parseAnswer = (
  answer: string | string[] | Record<string, string>,
  question: Question,
  allAnswers?: FieldValues,
) => {
  if (question.questionType === 'dynamicGrid') {
    return renderDynamicGridAnswer(answer, question);
  }
  if (question.questionType === 'dynamicTable') {
    const tableAnswer = resolveTableauLicenseUsersAnswer(
      question.name,
      answer,
      allAnswers ?? {},
    );
    return <DynamicTableSummary question={question} answer={tableAnswer} allAnswers={allAnswers} />;
  }
  if (Array.isArray(answer)) {
    return renderArrayAnswer(answer);
  }
  if (isJsonObject(answer)) {
    return renderJsonObject(answer as Record<string, string>, question);
  }
  if (
    question.questionType === 'costInput' &&
    (typeof answer === 'string' || typeof answer === 'number')
  ) {
    return formatCurrencyValue(answer);
  }
  if (typeof answer === 'string') {
    return <span style={{ whiteSpace: 'pre-line' }}>{answer}</span>;
  }
  return typeof answer === 'number' ? answer : '';
};

// ----------------------
// SHARED (unchanged) summary row renderer
// ----------------------
export function renderSummaryRow(
  question: Question,
  formattedAnswers:
    | string
    | string[]
    | Record<string, string>
    | (string | string[] | Record<string, string>)[],
  allAnswers?: FieldValues,
) {
  const isArrayOfAnswers = Array.isArray(formattedAnswers);
  const answersArray = isArrayOfAnswers ? formattedAnswers : [formattedAnswers];

  const multiple = answersArray.length > 1;
  const resolvedQuestionTitle =
    interpolateQuestionTextWithAnswerSums(question.title, question, allAnswers) || question.title;

  if (question.questionType === 'dynamicTable') {
    return (
      <tr className="govuk-table__row" key={question.name}>
        <td className="govuk-table__cell" colSpan={3} style={{ padding: '6px 0' }}>
          <h3 className="govuk-heading-m govuk-!-margin-top-2">{resolvedQuestionTitle}</h3>
          {parseAnswer(answersArray[0], { ...question, title: resolvedQuestionTitle }, allAnswers)}
        </td>
      </tr>
    );
  }

  return (
    <tr className="govuk-table__row" key={question.name}>
      <td className="govuk-table__cell" style={{ padding: '6px', width: '16%' }}>
        {resolvedQuestionTitle}
      </td>

      <td className="govuk-table__cell" colSpan={2} style={{ padding: '6px', width: '84%' }}>
        {!multiple ? (
          parseAnswer(answersArray[0], question, allAnswers)
        ) : (
          <ul className="govuk-list govuk-!-margin-0">
            {answersArray.map((ans, index) => (
              <li
                key={index}
                style={{
                  marginBottom: '10px',
                  paddingBottom: index < answersArray.length - 1 ? '10px' : '0',
                  borderBottom: index < answersArray.length - 1 ? '1px solid #b1b4b6' : 'none',
                }}
              >
                {parseAnswer(ans, question, allAnswers)}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

export function renderListAnswers(
  formValues: FieldValues,
  questions: Question[],
  listLabel?: string,
) {
  const grouped: Record<string, { question: Question; answer: unknown }[]> = {};

  Object.entries(formValues ?? {}).forEach(([questionName, answer]) => {
    const question = questions.find((q) => questionName.endsWith(q.name));
    if (!question) return;

    // Correct ID extraction based on real system: "1-questionName"
    const responseId = questionName.includes('-') ? questionName.split('-')[0] : '1';

    if (!grouped[responseId]) {
      grouped[responseId] = [];
    }
    grouped[responseId].push({ question, answer });
  });

  const sortedIds = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

  return (
    <>
      {sortedIds.map((id, index) => (
        <React.Fragment key={id}>
          <tr className="govuk-table__row">
            <td
              className="govuk-table__cell"
              colSpan={3}
              style={{
                padding: '6px',
                background: '#f3f2f1',
                fontWeight: 'bold',
                borderTop: '2px solid #b1b4b6',
              }}
            >
              {listLabel || 'Response'} {index + 1}
            </td>
          </tr>
          {grouped[id].map(({ question, answer }) => {
            const formatted = renderAnswer(question, answer);
            return (
              <React.Fragment key={question.name}>
                {renderSummaryRow(question, formatted, formValues)}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
}

export function renderListTotals(
  formValues: FieldValues,
  questions: Question[],
  total: ListTotalConfig,
) {
  if (!total.columns?.length) return null;

  const { groupBy, rows, hasEntries } = getListTotals(formValues, questions, total);
  if (!hasEntries) return null;

  const getQuestionTitle = (name: string) => {
    const question = questions.find((candidate) => candidate.name === name);
    const questionTitle = question?.title;
    return question
      ? interpolateQuestionTextWithAnswerSums(questionTitle, question, formValues) ||
          questionTitle ||
          name
      : interpolateQuestionText(questionTitle, formValues) || questionTitle || name;
  };

  return (
    <>
      <tr className="govuk-table__row">
        <td
          className="govuk-table__cell"
          colSpan={3}
          style={{ padding: '6px', fontWeight: 'bold', background: '#f3f2f1' }}
        >
          Totals
        </td>
      </tr>
      <tr className="govuk-table__row">
        <td className="govuk-table__cell" colSpan={3} style={{ padding: '6px' }}>
          <table className="govuk-table govuk-!-margin-0">
            <thead className="govuk-table__head">
              <tr className="govuk-table__row">
                {groupBy.map((fieldName) => (
                  <th className="govuk-table__header" key={`totals-group-header-${fieldName}`}>
                    {getQuestionTitle(fieldName)}
                  </th>
                ))}
                {total.columns.map((column) => (
                  <th className="govuk-table__header" key={`totals-col-header-${column.field}`}>
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="govuk-table__body">
              {rows.map((row) => (
                <tr className="govuk-table__row" key={`totals-row-${row.key}`}>
                  {groupBy.map((fieldName, index) => (
                    <td
                      className="govuk-table__cell"
                      key={`totals-group-value-${row.key}-${fieldName}`}
                    >
                      {row.groupValues[index]}
                    </td>
                  ))}
                  {row.totals.map((value, index) => (
                    <td className="govuk-table__cell" key={`totals-value-${row.key}-${index}`}>
                      {formatTotalValue(
                        value,
                        questions.find((question) => question.name === total.columns[index].field),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
}

// ----------------------
// FULL COMPONENT
// ----------------------
export const SummaryAnswers = ({
  pages,
  subsection,
  urlPath,
  displayTitle,
  data,
  disableChangeLinks = false,
  includeApprovalPage = false,
  includeListTotals = true,
  searchSuffix = '',
}: {
  pages: Page[];
  subsection: Subsection;
  urlPath: string;
  displayTitle?: boolean;
  data?: FetchFormResponse;
  disableChangeLinks?: boolean;
  includeApprovalPage?: boolean;
  includeListTotals?: boolean;
  searchSuffix?: string;
}) => {
  const answers: FetchFormResponse['answers'] = data?.answers || {};
  const { tenant, enabledSubsections } = useFormConfig();
  const gdadProficiencyDerivedAnswers =
    tenant === 'gdad' ? getGdadProficiencyDerivedAnswers(answers) : [];
  const getUniqueQuestionsByName = (questions: Question[]) =>
    questions.filter((question, index, all) => {
      return all.findIndex((candidate) => candidate.name === question.name) === index;
    });

  return (
    <table className="govuk-table">
      {displayTitle && (
        <caption className="govuk-table__caption govuk-table__caption--l">Your answers</caption>
      )}

      {pages.map((page) => {
        const { title, pageType } = page;
        if ((isApprovalPageName(page.name) || page.name === 'review') && !includeApprovalPage) {
          return null;
        }
        if (isPageDisabled(page, data || ({ answers: {} } as FetchFormResponse))) {
          return null;
        }

        const isDraft = !!data?.drafts?.[subsection.slug]?.includes(page.name);
        const questions = 'questions' in page ? page.questions || [] : [];
        const extractedQuestions = extractConditionalQuestions(questions);
        const listEntries = pageType === 'list' ? getListEntries(answers, extractedQuestions) : [];
        const listResponseCount = listEntries.length;
        const pageHasResponses =
          pageType === 'list'
            ? listResponseCount > 0
            : extractedQuestions.some(({ name }) =>
                Object.keys(answers).some((key) => key === name || key.startsWith(`${name}-`)),
              );
        const hideNilReturnQuestionPage =
          pageType === 'question' && page.type === 'nilreturn' && !pageHasResponses;

        if (hideNilReturnQuestionPage) {
          return null;
        }
        const hasSubmitGuardViolation = extractedQuestions.some((q) =>
          hasBlockedSubmitGuardValue(q, answers),
        );
        const listTotalConfig = page.total;
        const enabledPages = enabledSubsections[subsection.slug]?.pages?.map((p) => p.name) || [];
        const listHasIncompleteResponses =
          pageType === 'list' &&
          (hasRequiredListFieldsMissing(listEntries) ||
            (page.minimumRequiredListItems || 0) > listResponseCount ||
            (page.name === 'points-of-contact' &&
              extractedQuestions.some((q) => q.name === 'contacts-type') &&
              !hasPointsOfContactQueriesContact(listEntries)));

        let status: Status;

        if (page.type === 'nilreturn') {
          const hasDisableCondition =
            !!page.disabled && page.disabled.length > 0 && !!page.disabled[0];
          const controllingField = page.disabled?.[0]?.field;
          const controllingAnswered = controllingField
            ? hasValue(answers[controllingField])
            : false;
          const pageEnabled = enabledPages.includes(page.name);

          if (!hasDisableCondition) {
            status = Status.NilReturn;
          } else if (!controllingAnswered) {
            status = Status.NotStarted;
          } else if (!pageEnabled) {
            status = Status.NilReturn;
          } else if (pageType === 'list') {
            status =
              listResponseCount > 0 && !listHasIncompleteResponses
                ? Status.Completed
                : Status.InProgress;
          } else {
            const extracted = extractConditionalQuestions(questions);
            const hasResponses = extracted.some(({ name }) =>
              Object.keys(answers).some((key) => key === name || key.endsWith(name)),
            );
            const complete = isPageCompleted(extracted, answers);
            status = hasResponses && complete ? Status.Completed : Status.InProgress;
          }
        } else if (isDraft) {
          status = Status.InProgress;
        } else if (pageHasResponses) {
          status =
            hasSubmitGuardViolation || (pageType === 'list' && listHasIncompleteResponses)
              ? Status.InProgress
              : Status.Completed;
        } else {
          status = Status.NotStarted;
        }

        const normalisedUrlPath = urlPath.startsWith('/') ? urlPath : `/${urlPath}`;
        const linkTo = `/${tenant}${normalisedUrlPath}/${page.name}${searchSuffix}`;
        const linkLabel =
          pageType === 'information'
            ? 'View'
            : pageType === 'list'
              ? 'View'
              : pageHasResponses
                ? 'Change'
                : 'Go to Question';

        // ----- NilReturn list-page response label logic (mirrors Overview) -----
        let showCustomTag = false;
        let customTagText = '';

        if (pageType === 'list' && 'type' in page && (page as any).type === 'nilreturn') {
          const responseCount = listResponseCount;
          const label = page.listLabel || 'Response';
          const labelText = responseCount === 1 ? label : `${label}s`;

          if (responseCount === 0 && status === Status.NilReturn) {
            showCustomTag = true;
            customTagText = 'Nil Return';
          } else if (status === Status.InProgress) {
            showCustomTag = true;
            customTagText = 'In progress';
          } else if (
            responseCount > 0 &&
            (status === Status.Completed || status === Status.NilReturn)
          ) {
            showCustomTag = true;
            customTagText = `${responseCount} ${labelText}`;
          }
        }
        // -----------------------------------------------------------------------

        return (
          <tbody className="govuk-table__body" key={`${subsection.slug}-${page.name}`}>
            <tr className="govuk-table__row">
              <th
                scope="row"
                className="govuk-table__header govuk-!-width-one-half"
                style={{ paddingTop: 15, paddingBottom: 8, width: '16%' }}
              >
                {title}
              </th>

              <td className="govuk-table__cell" style={{ paddingTop: 15, paddingBottom: 8 }}>
                {pageType === 'question' && <Tag status={status} />}
                {pageType === 'list' && showCustomTag && (
                  <span className="govuk-tag">{customTagText}</span>
                )}
              </td>

              <td
                className="govuk-table__cell"
                style={{
                  textAlign: 'right',
                  paddingTop: 15,
                  paddingBottom: 8,
                }}
              >
                {disableChangeLinks ? (
                  // In read-only mode we suppress the action label entirely (otherwise "Change" is left behind).
                  <span className="govuk-body" aria-hidden="true" />
                ) : (
                  <Link to={linkTo} className="govuk-link">
                    {linkLabel}
                  </Link>
                )}
              </td>
            </tr>

            {includeListTotals &&
              listTotalConfig &&
              renderListTotals(answers, questions, listTotalConfig)}

            {/* QUESTION pages */}
            {tenant === 'gdad' &&
              pageType === 'question' &&
              page.name === 'proficiency-rating' &&
              gdadProficiencyDerivedAnswers.map((derivedAnswer) =>
                renderSummaryRow(
                  {
                    name: derivedAnswer.name,
                    title: derivedAnswer.title,
                    questionType: 'textInput',
                  },
                  derivedAnswer.value,
                  answers,
                ),
              )}
            {pageType === 'question' &&
              getUniqueQuestionsByName(questions).map(
                function renderRow(question): React.ReactNode {
                  if (question.questionType === 'content' || question.questionType === 'hidden') {
                    return null;
                  }

                  const allMatching = Object.keys(answers).filter((key) => key === question.name);

                  const formattedAnswers = allMatching.map((key) =>
                    renderAnswer(question, answers[key]),
                  );

                  const conditionalQuestions = getSelectedConditionalQuestions(
                    question,
                    answers[question.name],
                  );

                  return (
                    <React.Fragment key={question.name}>
                      {renderSummaryRow(question, formattedAnswers, answers)}

                      {conditionalQuestions.map(renderRow)}
                    </React.Fragment>
                  );
                },
              )}

            {/* LIST pages (grouped responses – including NilReturn lists) */}
            {pageType === 'list' &&
              renderListAnswers(answers, extractConditionalQuestions(questions), page.listLabel)}
          </tbody>
        );
      })}
    </table>
  );
};
